// src/components/ui/molecules/TagEditor.tsx
import React, { useState, useMemo } from 'react';
import { CategoryPicker } from './CategoryPicker';
import { TagPicker } from './TagPicker';
import type { TagProps } from '../atoms/Tag';

const normalizeLabel = (s: string) =>
  s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// map your API/category keys to the TagProps.category union
const CATEGORY_KEY_MAP: Record<string, TagProps['category']> = {
  DIET:            'diet',
  CUISINE:         'cuisine',
  MEAL_TYPE:       'meal',
  OCCASION:        'occasion',
  SEASON:          'season',
  NUTRITION:       'nutrition',
  COST_AND_EFFORT: 'cost',
  SPICE_LEVEL:     'spice',
};

export type Tag = {
  name: string;
  id: string;
  type: string;
  category: string;
};

export type TagEditorProps = {
  allTags: Tag[];            // flat list of tags, each with .category, .id, .name
  selectedTags: string[];    // array of tag IDs
  toggleTag: (tagId: string) => void;
};

export default function TagEditor({
  allTags,
  selectedTags,
  toggleTag,
}: TagEditorProps) {
  // group tags by their raw category string
  const tagsByCat = useMemo(() => {
    return allTags.reduce<Record<string, Tag[]>>((acc, t) => {
      acc[t.category] = acc[t.category] || [];
      acc[t.category].push(t);
      return acc;
    }, {});
  }, [allTags]);

  // build an array of category‐buttons
  const categories = Object.entries(tagsByCat).map(([key, tags]) => ({
    key,
    label: normalizeLabel(key),
  }));

  // which category is open
  const [selectedCategory, setSelectedCategory] = useState<string>(
    categories[0]?.key || ''
  );

  // how many tags are currently selected per raw category
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    selectedTags.forEach((tagId) => {
      const tag = allTags.find((t) => t.id === tagId);
      if (!tag) return;
      c[tag.category] = (c[tag.category] || 0) + 1;
    });
    return c;
  }, [selectedTags, allTags]);

  // figure out the correct `categoryKey` for TagPicker
  const categoryKey: TagProps['category'] =
    CATEGORY_KEY_MAP[selectedCategory] || 'diet';

  return (
    <div className="space-y-2">
      {/* show your category tabs (with counts badges) */}
      <CategoryPicker
        categories={categories}
        selected={selectedCategory}
        onSelect={setSelectedCategory}
        size="sm"
      />

      {/* show the tags for the open category */}
      <TagPicker
        categoryKey={categoryKey}
        tags={tagsByCat[selectedCategory] || []}
        selected={selectedTags}
        onToggle={toggleTag}
        size="sm"
      />
    </div>
  );
}
